import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Trash2,
  PanelTop,
  PanelLeft,
  Combine,
} from 'lucide-react';

// Floating toolbar that appears above the table whenever the cursor is in
// one of its cells. Position is computed off the table element itself, not
// the selection — so it anchors to the table top regardless of which cell
// you're in.
export function TableToolbar({ editor }: { editor: Editor | null }) {
  // useEditor doesn't re-render on selection-only transactions in v3, so
  // useEditorState is the right primitive to track "cursor entered a table".
  const inTable = useEditorState({
    editor,
    selector: ({ editor }) => Boolean(editor?.isActive('table')),
  });

  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!editor || !inTable) {
      setPos(null);
      return;
    }

    const compute = () => {
      // Walk up from the selection to find the enclosing table node, then
      // get its DOM element via the editor view to anchor the toolbar.
      const $from = editor.state.selection.$from;
      let tablePos: number | null = null;
      for (let d = $from.depth; d > 0; d--) {
        if ($from.node(d).type.name === 'table') {
          tablePos = $from.before(d);
          break;
        }
      }
      if (tablePos === null) return;
      const tableDom = editor.view.nodeDOM(tablePos) as HTMLElement | null;
      if (!tableDom) return;
      const rect = tableDom.getBoundingClientRect();
      // Park the toolbar 8 px above the table, left-aligned with it.
      setPos({ top: rect.top - 40, left: rect.left });
    };

    compute();

    const onScroll = () => compute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', compute);
    editor.on('selectionUpdate', compute);
    editor.on('update', compute);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', compute);
      editor.off('selectionUpdate', compute);
      editor.off('update', compute);
    };
  }, [editor, inTable]);

  if (!editor || !inTable || !pos) return null;

  const cmd = (op: () => void) => () => {
    op();
    // Re-focus the editor so the cursor stays in the table after a click.
    editor.view.focus();
  };

  return createPortal(
    <div
      role="toolbar"
      aria-label="Table actions"
      style={{ position: 'fixed', top: pos.top, left: pos.left }}
      className="z-50 inline-flex items-center gap-0.5 rounded-md border border-border bg-popover p-0.5 shadow-ambient no-print"
      data-no-print
      // Prevent click from blurring the editor (which would dismiss the toolbar).
      onMouseDown={(e) => e.preventDefault()}
    >
      <ToolGroup label="Row">
        <ToolBtn
          title="Insert row above"
          onClick={cmd(() => editor.chain().focus().addRowBefore().run())}
          icon={<ArrowUp className="size-3.5" />}
        />
        <ToolBtn
          title="Insert row below"
          onClick={cmd(() => editor.chain().focus().addRowAfter().run())}
          icon={<ArrowDown className="size-3.5" />}
        />
        <ToolBtn
          title="Toggle header row"
          onClick={cmd(() => editor.chain().focus().toggleHeaderRow().run())}
          icon={<PanelTop className="size-3.5" />}
        />
        <ToolBtn
          title="Delete row"
          danger
          onClick={cmd(() => editor.chain().focus().deleteRow().run())}
          icon={<Trash2 className="size-3.5" />}
        />
      </ToolGroup>
      <Divider />
      <ToolGroup label="Column">
        <ToolBtn
          title="Insert column left"
          onClick={cmd(() => editor.chain().focus().addColumnBefore().run())}
          icon={<ArrowLeft className="size-3.5" />}
        />
        <ToolBtn
          title="Insert column right"
          onClick={cmd(() => editor.chain().focus().addColumnAfter().run())}
          icon={<ArrowRight className="size-3.5" />}
        />
        <ToolBtn
          title="Toggle header column"
          onClick={cmd(() => editor.chain().focus().toggleHeaderColumn().run())}
          icon={<PanelLeft className="size-3.5" />}
        />
        <ToolBtn
          title="Delete column"
          danger
          onClick={cmd(() => editor.chain().focus().deleteColumn().run())}
          icon={<Trash2 className="size-3.5" />}
        />
      </ToolGroup>
      <Divider />
      <ToolBtn
        title="Merge / split cells"
        onClick={cmd(() => editor.chain().focus().mergeOrSplit().run())}
        icon={<Combine className="size-3.5" />}
      />
      <ToolBtn
        title="Delete table"
        danger
        onClick={cmd(() => editor.chain().focus().deleteTable().run())}
        icon={<Trash2 className="size-3.5" />}
      />
    </div>,
    document.body,
  );
}

function ToolGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex items-center gap-0.5">
      {children}
    </div>
  );
}

function ToolBtn({
  title,
  onClick,
  icon,
  danger,
}: {
  title: string;
  onClick: () => void;
  icon: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`inline-flex items-center justify-center size-7 rounded text-xs transition-colors ${
        danger
          ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      {icon}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-4 w-px bg-border" />;
}
